/**
 * useIsOperator.js — RC 1.62 Phase D completion (D-3).
 *
 * Single source of truth for "is the caller the platform operator?"
 * on the client. Reads the `is_operator` boolean projected by the
 * backend on `/api/auth/me/access` (employer + candidate workspaces
 * share this endpoint) so the frontend NEVER carries a hardcoded
 * operator UUID or email.
 *
 * Doctrine
 * ────────
 *   • Fail-closed: returns `false` on missing data, missing auth, or
 *     transport error. Never optimistically grants operator authority.
 *   • One fetch per mount; opt-in `refresh()` for hot paths that need
 *     fresh truth (e.g. after a workspace switch).
 *   • Loading state surfaces so route-level back-stops can defer
 *     navigation until truth resolves (avoids redirect storms).
 *
 * Why a hook (not a module-level cache)
 * ────────────────────────────────────
 *   React components need re-renders when the operator state
 *   resolves. A module-level cache would silently return `false` to
 *   the first paint and never re-trigger. The hook participates in
 *   React's lifecycle.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

const DORMANT = Object.freeze({
  isOperator: false,
  loading:    true,
  resolved:   false,
});

export function useIsOperator() {
  const [state, setState] = useState(DORMANT);

  const refresh = useCallback(async () => {
    try {
      const access = await apiFetch('/api/auth/me/access');
      setState({
        isOperator: Boolean(access?.is_operator),
        loading:    false,
        resolved:   true,
      });
    } catch {
      // Fail-closed. The route gate must treat unresolved as
      // "not operator" until proven otherwise.
      setState({ isOperator: false, loading: false, resolved: true });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...state, refresh };
}

export default useIsOperator;
